import { PCE_PALETTE_PRESETS, createPCEPaletteFrame, getPCEPalettePreset,
  type PCEPalettePresetId, type SceneAwarePCEResult, type PCESemanticContext } from '@limonade/pce-core';
import {
  exportCDL,
  exportCCC,
  LUTValidator,
  PCEExportManager,
  type PCEMatchAuditResult,
  type TargetedColorMatch,
} from '@limonade/eye-exporters';
import { LocalPCESemanticProvider } from './pceSemanticProvider.js';
import { PCE_MATCH_DEFAULTS, runPCEMatchPipeline, type PCEMatchSettings } from './pceMatchPipeline.js';

interface LoadedFrame {
  imageData: ImageData;
  name: string;
}

const MAX_DIMENSION = 720;
const percent = (value: number | undefined): string =>
  value === undefined ? '—' : `${Math.round(value * 100)}%`;
const signed = (value: number): string => `${value >= 0 ? '+' : ''}${value.toFixed(3)}`;
const rgbText = (rgb: { r: number; g: number; b: number } | undefined): string =>
  rgb ? `${rgb.r.toFixed(3)} / ${rgb.g.toFixed(3)} / ${rgb.b.toFixed(3)}` : '—';
const vectorText = (vectors: SceneAwarePCEResult['suggestedCorrections']['colorVectorMatch'] | undefined): string =>
  vectors?.vectors.length
    ? vectors.vectors.map((vector) =>
        `${vector.vector}: ${vector.hueShiftDegrees >= 0 ? '+' : ''}${vector.hueShiftDegrees.toFixed(1)}° (${Math.round(vector.confidence * 100)}%)`,
      ).join(' · ')
    : '—';
const exporter = new PCEExportManager();
const lutValidator = new LUTValidator();

type SelfAuditState = PCEMatchAuditResult;

interface PickedColor {
  rgb: [number, number, number];
  x: number;
  y: number;
}

interface TargetColorState {
  enabled: boolean;
  reference?: PickedColor;
  source?: PickedColor;
}

function fitDimensions(width: number, height: number): { width: number; height: number } {
  const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function loadFile(file: File, canvas: HTMLCanvasElement): Promise<LoadedFrame> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const size = fitDimensions(image.naturalWidth, image.naturalHeight);
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Could not create the PCE preview canvas.');
    context.drawImage(image, 0, 0, size.width, size.height);
    return { imageData: context.getImageData(0, 0, size.width, size.height), name: file.name };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function sampleFrame(canvas: HTMLCanvasElement, source: boolean): LoadedFrame {
  canvas.width = 480;
  canvas.height = 270;
  const context = canvas.getContext('2d', { willReadFrequently: true })!;
  const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
  if (source) {
    gradient.addColorStop(0, '#1b5435');
    gradient.addColorStop(1, '#234269');
  } else {
    gradient.addColorStop(0, '#287046');
    gradient.addColorStop(1, '#335e85');
  }
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = source ? '#a46f57' : '#c88b69';
  context.beginPath();
  context.ellipse(240, 132, 73, 98, 0, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = source ? '#d6c9ad' : '#eee3c7';
  context.fillRect(48, 43, 92, 58);
  context.fillStyle = source ? '#282b31' : '#353942';
  context.fillRect(338, 150, 94, 78);
  return {
    imageData: context.getImageData(0, 0, canvas.width, canvas.height),
    name: source ? 'Sample source' : 'Sample reference',
  };
}

function drawImageData(canvas: HTMLCanvasElement, imageData: ImageData): void {
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create preview canvas context.');
  context.putImageData(imageData, 0, 0);
}

function swatchStyle(color: PickedColor | undefined): string {
  if (!color) return 'background: transparent;';
  const [r, g, b] = color.rgb.map((value) => Math.round(value * 255));
  return `background: rgb(${r} ${g} ${b});`;
}

function colorText(color: PickedColor | undefined): string {
  if (!color) return 'Not picked';
  return color.rgb.map((value) => value.toFixed(3)).join(' / ');
}

function canvasPoint(event: MouseEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return {
    x: Math.max(0, Math.min(canvas.width - 1, Math.floor((event.clientX - rect.left) / rect.width * canvas.width))),
    y: Math.max(0, Math.min(canvas.height - 1, Math.floor((event.clientY - rect.top) / rect.height * canvas.height))),
  };
}

function pickAverageColor(imageData: ImageData, x: number, y: number, radius = 3): [number, number, number] {
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;
  for (let yy = Math.max(0, y - radius); yy <= Math.min(imageData.height - 1, y + radius); yy++) {
    for (let xx = Math.max(0, x - radius); xx <= Math.min(imageData.width - 1, x + radius); xx++) {
      const offset = (yy * imageData.width + xx) * 4;
      r += imageData.data[offset] ?? 0;
      g += imageData.data[offset + 1] ?? 0;
      b += imageData.data[offset + 2] ?? 0;
      count++;
    }
  }
  return [r / Math.max(1, count) / 255, g / Math.max(1, count) / 255, b / Math.max(1, count) / 255];
}

function targetedMatchesFromState(state: TargetColorState): TargetedColorMatch[] {
  if (!state.enabled || !state.reference || !state.source) return [];
  return [{
    label: 'manual_target_color',
    sourceRgb: state.source.rgb,
    targetRgb: state.reference.rgb,
    strength: 1,
    hueRadiusDegrees: 38,
    saturationRadius: 0.5,
    valueRadius: 0.58,
  }];
}

function currentPCEOptions(root: HTMLElement, workingSpace: HTMLSelectElement): PCEMatchSettings {
  return {
    ...PCE_MATCH_DEFAULTS,
    referencePresetId: (root.querySelector<HTMLSelectElement>('[data-pce="palette-preset"]')!.value || undefined) as PCEPalettePresetId | undefined,
    ignoreGreenscreen: root.querySelector<HTMLInputElement>('[data-pce="ignore-green"]')!.checked,
    protectSkin: root.querySelector<HTMLInputElement>('[data-pce="protect-skin"]')!.checked,
    matchMidtoneSkin: root.querySelector<HTMLInputElement>('[data-pce="match-midtones"]')!.checked,
    matchSixVectors: root.querySelector<HTMLInputElement>('[data-pce="six-vectors"]')!.checked,
    matchTones: root.querySelector<HTMLInputElement>('[data-pce="tonal-ranges"]')!.checked,
    localDetection: root.querySelector<HTMLInputElement>('[data-pce="use-detection"]')!.checked,
    referenceInputColorSpace: 'sRGB',
    sourceInputColorSpace: 'sRGB',
    workingSpace: workingSpace.value,
    quality: root.querySelector<HTMLSelectElement>('[data-pce="quality"]')!.value as PCEMatchSettings['quality'],
    lutSize: Number(root.querySelector<HTMLSelectElement>('[data-pce="lut-size"]')!.value) as 17 | 33,
    auditPasses: Number(root.querySelector<HTMLSelectElement>('[data-pce="audit-passes"]')!.value),
  };
}

function renderSelfAudit(
  root: HTMLElement,
  before: SceneAwarePCEResult,
  audit: SelfAuditState,
  duration: number,
): void {
  const set = (selector: string, value: string): void => {
    const element = root.querySelector<HTMLElement>(selector);
    if (element) element.textContent = value;
  };
  const after = audit.result;
  const improvement = after.overallMatchScore - before.overallMatchScore;
  const temperatureResidual = after.suggestedCorrections.temperatureShift;
  const tintResidual = after.suggestedCorrections.tintShift;
  const exposureResidual = after.suggestedCorrections.exposureShift;
  const whitePoint = after.suggestedCorrections.whitePoint;
  const blackPoint = after.suggestedCorrections.blackPoint;
  const saturationMatch = after.suggestedCorrections.saturationMatch;
  const skinToneMatch = after.suggestedCorrections.skinToneMatch;
  const verdict = improvement > 0.025
    ? 'LUT improved the match'
    : improvement < -0.015
      ? 'LUT made the match worse'
      : 'LUT change is subtle / neutral';

  set('[data-pce-audit="before"]', percent(before.overallMatchScore));
  set('[data-pce-audit="description"]', audit.report.scoreKind === 'palette-adherence'
    ? 'Applies the exact exported CUBE and measures palette color adherence, not scene similarity. This creative look affects the whole image, including skin. Highlight clipping is still checked.'
    : 'Applies the actual exported CUBE table, re-matches against the reference, and keeps improving passes. Protected skin and highlight clipping are checked before accepting a pass.');
  set('[data-pce-audit="after"]', percent(after.overallMatchScore));
  set('[data-pce-audit="delta"]', `${improvement >= 0 ? '+' : ''}${Math.round(improvement * 100)} pts`);
  set('[data-pce-audit="verdict"]', verdict);
  set('[data-pce-audit="duration"]', `${duration.toFixed(1)} ms`);
  set('[data-pce-audit="temperature"]', signed(temperatureResidual));
  set('[data-pce-audit="tint"]', signed(tintResidual));
  set('[data-pce-audit="exposure"]', signed(exposureResidual));
  set('[data-pce-audit="white-confidence"]', percent(whitePoint?.confidence));
  set('[data-pce-audit="white-gains"]', whitePoint ? whitePoint.channelGains.map((value) => value.toFixed(3)).join(' / ') : '—');
  set('[data-pce-audit="black-confidence"]', percent(blackPoint?.confidence));
  set('[data-pce-audit="black-offsets"]', blackPoint ? blackPoint.channelOffsets.map((value) => value.toFixed(3)).join(' / ') : '—');
  set('[data-pce-audit="sat-ratio"]', saturationMatch ? `${saturationMatch.saturationRatio.toFixed(3)}×` : '—');
  set('[data-pce-audit="skin-power"]', skinToneMatch ? skinToneMatch.channelPower.map((value) => value.toFixed(3)).join(' / ') : '—');
  set('[data-pce-audit="skin-confidence"]', percent(skinToneMatch?.confidence));
  set('[data-pce-audit="vectors"]', vectorText(after.suggestedCorrections.colorVectorMatch));

  const list = root.querySelector<HTMLUListElement>('[data-pce-audit="notes"]')!;
  list.replaceChildren();
  const notes = [
    audit.report.scoreKind === 'palette-adherence' ? 'Score measures palette color adherence, not scene similarity. The palette is applied globally, including skin.' : '',
    ...audit.report.passes.map((pass) => `Pass ${pass.pass}: ${pass.reason} Score ${percent(pass.matchScore)} (${pass.improvement >= 0 ? '+' : ''}${(pass.improvement * 100).toFixed(1)} points).`),
    ...audit.report.warnings,
    Math.abs(temperatureResidual) >= 2 ? 'Temperature correction still has visible residual after the first LUT.' : '',
    Math.abs(tintResidual) >= 2 ? 'Tint correction still has visible residual after the first LUT.' : '',
    Math.abs(exposureResidual) >= 0.05 ? 'Exposure correction still has residual after the first LUT.' : '',
    `Kept ${audit.report.acceptedPasses} automatic pass(es). Preview uses the exact best exported CUBE table.`,
  ].filter(Boolean);
  notes.forEach((note) => {
    const item = document.createElement('li');
    item.textContent = note;
    if (improvement > 0.025 && note.startsWith('Generated')) item.className = 'clear';
    list.append(item);
  });
  root.querySelector<HTMLButtonElement>('[data-pce-download="cube-v2"]')!.disabled = false;
  set('[data-pce-audit="raw"]', JSON.stringify(audit.report, null, 2));
}

function renderSixVectors(root: HTMLElement, result: SceneAwarePCEResult): void {
  const body = root.querySelector<HTMLTableSectionElement>('[data-pce="vector-table"]')!;
  body.replaceChildren();
  const vectors = result.suggestedCorrections.colorVectorMatch;
  for (const vector of vectors?.vectors ?? []) {
    const row = document.createElement('tr');
    const supported = vector.referenceSampleCount > 0 && vector.sourceSampleCount > 0 && vector.confidence >= 0.16;
    for (const value of [vector.vector, vector.sourceHue === null ? '—' : `${vector.sourceHue.toFixed(1)}°`,
      vector.referenceHue === null ? '—' : `${vector.referenceHue.toFixed(1)}°`,
      supported ? `${vector.hueShiftDegrees >= 0 ? '+' : ''}${vector.hueShiftDegrees.toFixed(1)}°` : 'No reliable pair',
      percent(vector.confidence), `${vector.referenceSampleCount} / ${vector.sourceSampleCount}`]) {
      const cell = document.createElement('td');
      cell.textContent = value;
      row.append(cell);
    }
    body.append(row);
  }
  if (!vectors) {
    const row = body.insertRow();
    const cell = row.insertCell();
    cell.colSpan = 6;
    cell.textContent = 'Six-vector matching is disabled.';
  }
}

function renderTonalMatching(root: HTMLElement, before?: SceneAwarePCEResult, after?: SceneAwarePCEResult): void {
  const analysis = before?.tonalAnalysis;
  const state = root.querySelector<HTMLElement>('[data-pce="tonal-state"]')!;
  state.textContent = !before ? 'Re-analyze to update' : analysis ? 'Soft tonal masks · deterministic CV' : 'Tone-aware layer disabled';
  const cards = root.querySelector<HTMLElement>('[data-pce="tonal-cards"]')!;
  cards.replaceChildren();
  for (const band of analysis?.bands ?? []) {
    const card = document.createElement('article');
    const title = document.createElement('h4');
    title.textContent = band.range;
    card.append(title);
    const measuredAfter = after?.tonalAnalysis?.bands.find((entry) => entry.range === band.range);
    const rgbBias = (values: number[]): string => values.map((value) => signed(value)).join(' / ');
    const metrics = [
      ['Match · before → audited', `${percent(band.matchScore ?? undefined)} → ${percent(measuredAfter?.matchScore ?? undefined)}`],
      ['Coverage · ref / source', `${band.reference.coveragePercentage.toFixed(1)}% / ${band.source.coveragePercentage.toFixed(1)}%`],
      ['Luminance · ref / source', `${band.reference.meanLuminance.toFixed(3)} / ${band.source.meanLuminance.toFixed(3)}`],
      ['Saturation · ref / source', `${percent(band.reference.meanSaturation)} / ${percent(band.source.meanSaturation)}`],
      ['Range confidence', percent(band.confidence)],
      ['Reference color bias · R/G/B', rgbBias(band.reference.colorBias)],
      ['Source color bias · R/G/B', rgbBias(band.source.colorBias)],
    ];
    for (const [label, value] of metrics) {
      const item = document.createElement('div');
      const small = document.createElement('small');
      small.textContent = label!;
      const strong = document.createElement('strong');
      strong.textContent = value!;
      item.append(small, strong);
      card.append(item);
    }
    cards.append(card);
  }
  const body = root.querySelector<HTMLTableSectionElement>('[data-pce="tonal-vector-table"]')!;
  body.replaceChildren();
  const range = root.querySelector<HTMLSelectElement>('[data-pce="tonal-view"]')!.value;
  const selected = analysis?.bands.find((band) => band.range === range);
  if (!selected || !analysis?.sixVectorEnabled) {
    const cell = body.insertRow().insertCell();
    cell.colSpan = 6;
    cell.textContent = analysis ? 'Six-vector matching is disabled.' : before ? 'Enable tone-aware matching and re-analyze.' : 'Analyze to inspect six colors within each tonal range.';
    return;
  }
  for (const vector of selected.colorVectors.vectors) {
    const supported = selected.confidence >= 0.25 && vector.confidence >= 0.25;
    const row = body.insertRow();
    for (const value of [vector.vector,
      vector.sourceHue === null ? '—' : `${vector.sourceHue.toFixed(1)}°`,
      vector.referenceHue === null ? '—' : `${vector.referenceHue.toFixed(1)}°`,
      supported ? `${vector.hueShiftDegrees >= 0 ? '+' : ''}${vector.hueShiftDegrees.toFixed(1)}°` : 'No reliable pair',
      percent(vector.confidence * selected.confidence), `${vector.referenceSampleCount} / ${vector.sourceSampleCount}`]) row.insertCell().textContent = value;
  }
}

function renderDetectionOverlays(root: HTMLElement, context: PCESemanticContext | undefined, show: boolean): void {
  for (const side of ['reference', 'source'] as const) {
    const canvas = root.querySelector<HTMLCanvasElement>(`[data-pce="${side}-detections"]`)!;
    const original = root.querySelector<HTMLCanvasElement>(`[data-pce="${side}-canvas"]`)!;
    canvas.width = original.width;
    canvas.height = original.height;
    const drawing = canvas.getContext('2d')!;
    drawing.clearRect(0, 0, canvas.width, canvas.height);
    const segmentation = context?.[side];
    if (!show || !segmentation) continue;
    const data = new Uint8ClampedArray(segmentation.width * segmentation.height * 4);
    const skin = segmentation.regions.find((region) => region.label === 'skin')?.mask;
    const person = segmentation.regions.find((region) => region.label === 'person')?.mask;
    for (let index = 0; index < segmentation.width * segmentation.height; index++) {
      const isSkin = (skin?.[index] ?? 0) >= 166;
      if (!isSkin && (person?.[index] ?? 0) < 166) continue;
      data.set(isSkin ? [223, 255, 87, 100] : [85, 223, 255, 45], index * 4);
    }
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = segmentation.width;
    maskCanvas.height = segmentation.height;
    maskCanvas.getContext('2d')!.putImageData(new ImageData(data, segmentation.width, segmentation.height), 0, 0);
    drawing.drawImage(maskCanvas, 0, 0, canvas.width, canvas.height);
    maskCanvas.width = 1;
    maskCanvas.height = 1;
    drawing.strokeStyle = '#dfff57';
    drawing.fillStyle = '#dfff57';
    drawing.lineWidth = 2;
    drawing.font = '12px sans-serif';
    for (const [index, face] of (segmentation.detection?.faces ?? []).entries()) {
      drawing.strokeRect(face.x * canvas.width, face.y * canvas.height, face.width * canvas.width, face.height * canvas.height);
      drawing.fillText(`Face ${index + 1} · ${percent(face.confidence)}`, face.x * canvas.width, Math.max(14, face.y * canvas.height - 5));
    }
  }
}

function renderDetectionSummary(root: HTMLElement, result: SceneAwarePCEResult): void {
  const semantic = result.semanticAnalysis;
  root.querySelector<HTMLElement>('[data-pce="detection-mode"]')!.textContent = semantic?.mode === 'semantic' ? 'Local AI masks active' : semantic?.mode === 'partial' ? 'Partial detection / fallback' : 'Deterministic color heuristics';
  for (const side of ['reference', 'source'] as const) {
    const detection = semantic?.[side];
    root.querySelector<HTMLElement>(`[data-pce="${side}-faces"]`)!.textContent = detection ? String(detection.faces.length) : '—';
    root.querySelector<HTMLElement>(`[data-pce="${side}-person"]`)!.textContent = percent(detection?.personCoverage);
    root.querySelector<HTMLElement>(`[data-pce="${side}-skin"]`)!.textContent = percent(detection?.skinCoverage);
  }
  root.querySelector<HTMLElement>('[data-pce="skin-mode"]')!.textContent = semantic?.skinMatching ?? 'color-heuristic';
}

function renderResult(root: HTMLElement, result: SceneAwarePCEResult, duration: number, size: 17 | 33): void {
  const set = (selector: string, value: string): void => {
    const element = root.querySelector<HTMLElement>(selector);
    if (element) element.textContent = value;
  };
  set('[data-pce="overall"]', percent(result.overallMatchScore));
  set('[data-pce="overall-label"]', result.paletteAnalysis ? 'Palette color adherence' : 'Overall');
  set('[data-pce="hue"]', percent(result.hueMatchScore));
  set('[data-pce="saturation"]', percent(result.saturationMatchScore));
  set('[data-pce="luminance"]', percent(result.luminanceMatchScore));
  set('[data-pce="confidence"]', percent(result.confidence));
  set('[data-pce="duration"]', `${duration.toFixed(1)} ms`);
  set('[data-pce="skin"]', percent(result.regionScores.skin));
  set('[data-pce="neutrals"]', percent(result.regionScores.neutrals));
  set('[data-pce="highlights"]', percent(result.regionScores.highlights));
  set('[data-pce="shadows"]', percent(result.regionScores.shadows));
  set('[data-pce="background"]', percent(result.regionScores.background));
  set('[data-pce="greenscreen"]', percent(result.regionScores.greenscreen));
  set('[data-pce="temperature"]', signed(result.suggestedCorrections.temperatureShift));
  set('[data-pce="tint"]', signed(result.suggestedCorrections.tintShift));
  set('[data-pce="exposure"]', signed(result.suggestedCorrections.exposureShift));
  set('[data-pce="sat-shift"]', signed(result.suggestedCorrections.saturationShift));
  set('[data-pce="contrast"]', signed(result.suggestedCorrections.contrastShift));
  set('[data-pce="white-reference"]', rgbText(result.suggestedCorrections.whitePoint?.reference));
  set('[data-pce="white-source"]', rgbText(result.suggestedCorrections.whitePoint?.source));
  set('[data-pce="white-gains"]', result.suggestedCorrections.whitePoint
    ? result.suggestedCorrections.whitePoint.channelGains.map((value) => value.toFixed(3)).join(' / ')
    : '—');
  set('[data-pce="white-confidence"]', percent(result.suggestedCorrections.whitePoint?.confidence));
  set('[data-pce="black-reference"]', rgbText(result.suggestedCorrections.blackPoint?.reference));
  set('[data-pce="black-source"]', rgbText(result.suggestedCorrections.blackPoint?.source));
  set('[data-pce="black-offsets"]', result.suggestedCorrections.blackPoint
    ? result.suggestedCorrections.blackPoint.channelOffsets.map((value) => value.toFixed(3)).join(' / ')
    : '—');
  set('[data-pce="black-confidence"]', percent(result.suggestedCorrections.blackPoint?.confidence));
  set('[data-pce="sat-ratio"]', result.suggestedCorrections.saturationMatch ? `${result.suggestedCorrections.saturationMatch.saturationRatio.toFixed(3)}×` : '—');
  set('[data-pce="sat-measured"]', result.suggestedCorrections.saturationMatch
    ? `${result.suggestedCorrections.saturationMatch.referenceMean.toFixed(3)} / ${result.suggestedCorrections.saturationMatch.sourceMean.toFixed(3)}`
    : '—');
  set('[data-pce="sat-confidence"]', percent(result.suggestedCorrections.saturationMatch?.confidence));
  set('[data-pce="skin-reference"]', rgbText(result.suggestedCorrections.skinToneMatch?.reference));
  set('[data-pce="skin-source"]', rgbText(result.suggestedCorrections.skinToneMatch?.source));
  set('[data-pce="skin-power"]', result.suggestedCorrections.skinToneMatch
    ? result.suggestedCorrections.skinToneMatch.channelPower.map((value) => value.toFixed(3)).join(' / ')
    : '—');
  set('[data-pce="skin-confidence"]', percent(result.suggestedCorrections.skinToneMatch?.confidence));
  set('[data-pce="vector-confidence"]', percent(result.suggestedCorrections.colorVectorMatch?.confidence));
  set('[data-pce="vector-shifts"]', vectorText(result.suggestedCorrections.colorVectorMatch));
  set('[data-pce="cmf-working"]', result.colorManagement.workingColorSpace);
  set('[data-pce="cmf-input"]', `${result.colorManagement.inputColorSpace.reference} / ${result.colorManagement.inputColorSpace.source}`);
  set('[data-pce="cmf-managed"]', result.colorManagement.pceColorManaged ? 'Yes' : 'Default');

  const messages = root.querySelector<HTMLUListElement>('[data-pce="messages"]')!;
  messages.replaceChildren();
  const items = [...result.warnings, ...result.recommendations];
  (items.length ? items : ['No scene-aware warnings for this comparison.']).forEach((message) => {
    const item = document.createElement('li');
    item.textContent = message;
    if (!items.length) item.className = 'clear';
    messages.append(item);
  });
  set('[data-pce="raw"]', JSON.stringify(result, null, 2));

  const report = exporter.generateReport(result, { size });
  const cdl = exporter.generateCDL(result);
  const lut = exporter.generateCDLCubeLUT(result, { size, title: 'pre match' });
  const validation = lutValidator.validateCube(lut);
  set('[data-pce-export="cdl-slope"]', cdl.slope.map((value) => value.toFixed(4)).join('  '));
  set('[data-pce-export="cdl-offset"]', cdl.offset.map((value) => value.toFixed(4)).join('  '));
  set('[data-pce-export="cdl-power"]', cdl.power.map((value) => value.toFixed(4)).join('  '));
  set('[data-pce-export="cdl-saturation"]', cdl.saturation.toFixed(4));
  set('[data-pce-export="lut"]', validation.valid ? `${size}³ CUBE ready` : `Needs review · ${validation.warnings.length} warning(s)`);
  set('[data-pce-export="confidence"]', percent(cdl.confidence));
  set('[data-pce-export="report"]', JSON.stringify(report, null, 2));

  const suggestionList = root.querySelector<HTMLUListElement>('[data-pce-export="suggestions"]')!;
  suggestionList.replaceChildren();
  report.gradingSuggestions.forEach((suggestion) => {
    const item = document.createElement('li');
    item.textContent = suggestion;
    suggestionList.append(item);
  });

  root.querySelectorAll<HTMLButtonElement>('[data-pce-download]').forEach((button) => {
    if (button.dataset.pceDownload !== 'cube-v2') button.disabled = false;
    if (result.paletteAnalysis && !result.suggestedCorrections.paletteCorrection?.monochrome && ['cc', 'ccc'].includes(button.dataset.pceDownload ?? '')) button.disabled = true;
  });
}

function downloadText(filename: string, mimeType: string, content: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function mountPCEPlayground(root: HTMLElement): void {
  root.innerHTML = `
    <div class="pce-heading">
      <div>
        <div class="eyebrow">PCE · PERCEPTUAL COLOR ENGINE</div>
        <h2>Reference / Source Match</h2>
        <p>Match a reference image or a built-in color palette, then audit the exported LUT locally.</p>
      </div>
      <div class="pce-provider-state">CMF v0.2 · optional local MediaPipe · audited LUT</div>
    </div>
    <div class="pce-input-grid">
      <div class="pce-frame-card">
        <span data-pce="reference-title">Reference image</span>
        <label class="pce-select-label">Match reference
          <select data-pce="palette-preset" aria-label="Match reference">
            <option value="">Uploaded image</option>
            <optgroup label="Built-in palette presets">${PCE_PALETTE_PRESETS.map((preset) => `<option value="${preset.id}">${preset.name}</option>`).join('')}</optgroup>
          </select>
        </label>
        <input data-pce="reference-input" type="file" accept="image/*" />
        <small data-pce="preset-description" hidden></small>
        <div class="pce-frame-stage"><canvas data-pce="reference-canvas" aria-label="PCE reference image preview"></canvas><canvas data-pce="reference-detections" class="pce-detection-overlay" aria-label="Reference face and person overlay"></canvas></div>
        <small data-pce="reference-name">Choose an image</small>
      </div>
      <div class="pce-frame-card">
        <span>Source image</span>
        <input data-pce="source-input" type="file" accept="image/*" />
        <div class="pce-frame-stage"><canvas data-pce="source-canvas" aria-label="PCE source image preview"></canvas><canvas data-pce="source-detections" class="pce-detection-overlay" aria-label="Source face and person overlay"></canvas></div>
        <small data-pce="source-name">Choose an image</small>
      </div>
      <div class="pce-frame-card pce-audit-preview">
        <span>LUT-applied source preview</span>
        <canvas data-pce-audit="preview-canvas" aria-label="PCE LUT-applied source preview"></canvas>
        <small>Generated after PCE self-audit</small>
      </div>
    </div>
    <div class="pce-controls">
      <label><input data-pce="ignore-green" type="checkbox" checked /> Ignore greenscreen</label>
      <label><input data-pce="protect-skin" type="checkbox" checked /> Protect skin</label>
      <label><input data-pce="match-midtones" type="checkbox" checked /> Match skin/midtones</label>
      <label><input data-pce="six-vectors" type="checkbox" checked /> Six-vector hue matching</label>
      <label><input data-pce="tonal-ranges" type="checkbox" checked /> Tone-aware matching</label>
      <label><input data-pce="use-detection" type="checkbox" checked /> Detect faces / people locally</label>
      <label><input data-pce="show-detection" type="checkbox" checked /> Show detection masks</label>
      <label><input data-pce-target="enabled" type="checkbox" checked /> Use target color match</label>
      <label class="pce-select-label">Working space
        <select data-pce="working-space">
          <option value="OKLab-analysis-from-sRGB">Default OKLab/sRGB</option>
          <option value="sRGB">sRGB</option>
          <option value="Rec709">Rec.709</option>
          <option value="DisplayP3">Display P3</option>
          <option value="ACEScg">ACEScg</option>
        </select>
      </label>
      <button data-pce="sample" type="button" class="secondary-button">Load sample pair</button>
      <label class="pce-select-label">Audit passes <select data-pce="audit-passes"><option value="1">1</option><option value="2" selected>2</option><option value="3">3</option></select></label>
      <label class="pce-select-label">Analysis detail <select data-pce="quality"><option value="quality">Fine</option><option value="balanced" selected>Balanced</option><option value="realtime">Fast</option></select></label>
      <label class="pce-select-label">LUT precision <select data-pce="lut-size"><option value="17">17 · smaller</option><option value="33" selected>33 · finer</option></select></label>
      <button data-pce="analyze" type="button" disabled>Analyze → Audit → Re-match</button>
      <button data-pce="rematch" type="button" class="secondary-button" disabled>Re-match from original</button>
      <span data-pce="status">Load a source and choose a reference image or palette preset.</span>
    </div>
    <section class="pce-export-panel">
      <div class="panel-title-row"><div><h3>Face &amp; Person Detection</h3><p>Face boxes: lime · skin mask: lime · person mask: cyan. Detection guides skin sampling; person coverage is not a person count.</p></div><span data-pce="detection-mode">Waiting for analysis</span></div>
      <div class="pce-metric-grid">
        <div><small>Reference faces</small><strong data-pce="reference-faces">—</strong></div>
        <div><small>Source faces</small><strong data-pce="source-faces">—</strong></div>
        <div><small>Skin matching</small><strong data-pce="skin-mode">—</strong></div>
        <div><small>Reference person coverage</small><strong data-pce="reference-person">—</strong></div>
        <div><small>Source person coverage</small><strong data-pce="source-person">—</strong></div>
        <div><small>Reference / source skin</small><strong><span data-pce="reference-skin">—</span> / <span data-pce="source-skin">—</span></strong></div>
      </div>
    </section>
    <section class="pce-export-panel">
      <div class="panel-title-row"><div><h3>Shadows · Midtones · Highlights</h3><p>Soft luminance masks compare each range independently. Six-vector hues can shift differently in shadows and highlights; skin can occur in any range.</p></div><span data-pce="tonal-state">Waiting for analysis</span></div>
      <div class="pce-tonal-grid" data-pce="tonal-cards"></div>
      <label class="pce-select-label">Inspect tonal color vectors <select data-pce="tonal-view"><option value="shadows">Shadows</option><option value="midtones" selected>Midtones</option><option value="highlights">Highlights</option></select></label>
      <div class="pce-table-scroll"><table class="pce-vector-table"><thead><tr><th>Color</th><th>Source hue</th><th>Reference hue</th><th>Measured difference</th><th>Confidence</th><th>Ref / source samples</th></tr></thead><tbody data-pce="tonal-vector-table"><tr><td colspan="6">Analyze to inspect six colors within each tonal range.</td></tr></tbody></table></div>
      <p class="pce-tonal-note">Tone-aware mode replaces the automatic global hue curve with three softly blended curves. Measured differences are not full-strength LUT corrections. Sparse ranges and clipped/near-black color evidence are suppressed. Color bias may reflect objects, not just lighting. CDL still supplies conservative exposure, black/white balance, and skin adjustments.</p>
    </section>
    <section class="pce-export-panel">
      <div class="panel-title-row"><div><h3>Six-Vector Color Match</h3><p>Red, yellow, green, cyan, blue, and magenta build a Hue vs Hue curve. Missing colors receive no inferred correction; colors in different scenes may belong to different objects.</p></div><span>HUE VS HUE</span></div>
      <div class="pce-table-scroll"><table class="pce-vector-table"><thead><tr><th>Color</th><th>Source hue</th><th>Reference hue</th><th>Suggested shift</th><th>Confidence</th><th>Ref / source samples</th></tr></thead><tbody data-pce="vector-table"><tr><td colspan="6">Run a match to inspect color vectors.</td></tr></tbody></table></div>
    </section>
    <section class="pce-export-panel pce-target-panel">
      <div class="panel-title-row">
        <div>
          <h3>Target Color Match</h3>
          <p>Pick a color in the reference and the matching color in the source. The .cube LUT will locally push source color toward reference.</p>
        </div>
        <span>MANUAL TARGET</span>
      </div>
      <div class="pce-export-actions">
        <button data-pce-target="pick-reference" type="button" class="secondary-button">Pick reference target</button>
        <button data-pce-target="pick-source" type="button" class="secondary-button">Pick source target</button>
        <button data-pce-target="clear" type="button" class="secondary-button">Clear target match</button>
      </div>
      <div class="pce-export-grid">
        <article>
          <small>Reference target</small>
          <span class="color-swatch" data-pce-target="reference-swatch"></span>
          <strong data-pce-target="reference-rgb">Not picked</strong>
        </article>
        <article>
          <small>Source target</small>
          <span class="color-swatch" data-pce-target="source-swatch"></span>
          <strong data-pce-target="source-rgb">Not picked</strong>
        </article>
        <article class="wide">
          <small>Status</small>
          <strong data-pce-target="status">Pick both colors to enable targeted LUT correction.</strong>
        </article>
      </div>
    </section>
    <div class="pce-results">
      <section>
        <h3>Match scores</h3>
        <div class="pce-metric-grid">
          <div><small data-pce="overall-label">Overall</small><strong data-pce="overall">—</strong></div>
          <div><small>Hue</small><strong data-pce="hue">—</strong></div>
          <div><small>Saturation</small><strong data-pce="saturation">—</strong></div>
          <div><small>Luminance</small><strong data-pce="luminance">—</strong></div>
          <div><small>Confidence</small><strong data-pce="confidence">—</strong></div>
          <div><small>Processing</small><strong data-pce="duration">—</strong></div>
        </div>
      </section>
      <section>
        <h3>Suggested corrections</h3>
        <div class="pce-metric-grid">
          <div><small>Temperature</small><strong data-pce="temperature">—</strong></div>
          <div><small>Tint</small><strong data-pce="tint">—</strong></div>
          <div><small>Exposure</small><strong data-pce="exposure">—</strong></div>
          <div><small>Saturation</small><strong data-pce="sat-shift">—</strong></div>
          <div><small>Contrast</small><strong data-pce="contrast">—</strong></div>
          <div><small>Ref white RGB</small><strong data-pce="white-reference">—</strong></div>
          <div><small>Source white RGB</small><strong data-pce="white-source">—</strong></div>
          <div><small>White gains</small><strong data-pce="white-gains">—</strong></div>
          <div><small>White confidence</small><strong data-pce="white-confidence">—</strong></div>
          <div><small>Ref black RGB</small><strong data-pce="black-reference">—</strong></div>
          <div><small>Source black RGB</small><strong data-pce="black-source">—</strong></div>
          <div><small>Black offsets</small><strong data-pce="black-offsets">—</strong></div>
          <div><small>Black confidence</small><strong data-pce="black-confidence">—</strong></div>
          <div><small>Measured sat ref/src</small><strong data-pce="sat-measured">—</strong></div>
          <div><small>Sat ratio</small><strong data-pce="sat-ratio">—</strong></div>
          <div><small>Sat confidence</small><strong data-pce="sat-confidence">—</strong></div>
          <div><small>Ref skin RGB</small><strong data-pce="skin-reference">—</strong></div>
          <div><small>Source skin RGB</small><strong data-pce="skin-source">—</strong></div>
          <div><small>Skin/midtone power</small><strong data-pce="skin-power">—</strong></div>
          <div><small>Skin confidence</small><strong data-pce="skin-confidence">—</strong></div>
          <div><small>6-vector confidence</small><strong data-pce="vector-confidence">—</strong></div>
          <div class="wide"><small>6-vector Hue-vs-Hue</small><strong data-pce="vector-shifts">—</strong></div>
        </div>
      </section>
      <section>
        <h3>CMF status</h3>
        <div class="pce-metric-grid">
          <div><small>Input spaces</small><strong data-pce="cmf-input">—</strong></div>
          <div><small>Working</small><strong data-pce="cmf-working">—</strong></div>
          <div><small>Managed</small><strong data-pce="cmf-managed">—</strong></div>
        </div>
      </section>
      <section>
        <h3>Scene-aware regions</h3>
        <div class="pce-metric-grid">
          <div><small>Skin</small><strong data-pce="skin">—</strong></div>
          <div><small>Neutrals</small><strong data-pce="neutrals">—</strong></div>
          <div><small>Highlights</small><strong data-pce="highlights">—</strong></div>
          <div><small>Shadows</small><strong data-pce="shadows">—</strong></div>
          <div><small>Background</small><strong data-pce="background">—</strong></div>
          <div><small>Greenscreen</small><strong data-pce="greenscreen">—</strong></div>
        </div>
      </section>
      <section>
        <h3>Warnings & recommendations</h3>
        <ul data-pce="messages"><li>Run a comparison to see diagnostics.</li></ul>
      </section>
    </div>
    <section class="pce-export-panel">
      <div class="panel-title-row">
        <div>
          <h3>Latest Export Tools</h3>
          <p>Generate local grading suggestions from the latest PCE result. CDL/LUT files are not streamed anywhere.</p>
        </div>
        <span>LUT/CDL v0.1</span>
      </div>
      <div class="pce-export-grid">
        <article>
          <small>ASC CDL</small>
          <strong>Slope <span data-pce-export="cdl-slope">—</span></strong>
          <strong>Offset <span data-pce-export="cdl-offset">—</span></strong>
          <strong>Power <span data-pce-export="cdl-power">—</span></strong>
          <strong>Saturation <span data-pce-export="cdl-saturation">—</span></strong>
        </article>
        <article>
          <small>CUBE LUT</small>
          <strong data-pce-export="lut">Waiting for PCE result</strong>
          <span>Selected 17³ / 33³ precision. Final LUT export uses the exact audited preview; CDL is an initial suggestion, not the composed multi-pass LUT.</span>
          <em>Confidence: <b data-pce-export="confidence">—</b></em>
        </article>
        <article class="wide">
          <small>Rule-based Grade Suggestions</small>
          <ul data-pce-export="suggestions"><li>Run a PCE match to see export suggestions.</li></ul>
        </article>
      </div>
      <div class="pce-export-actions">
        <button data-pce-download="cube" type="button" disabled>Download first-pass .cube</button>
        <button data-pce-download="cube-v2" type="button" disabled>Download best audited .cube</button>
        <button data-pce-download="cc" type="button" disabled>Download ASC .cc CDL</button>
        <button data-pce-download="ccc" type="button" disabled>Download ASC .ccc Collection</button>
        <button data-pce-download="report" type="button" disabled>Download Export Report</button>
      </div>
      <details class="pce-export-json">
        <summary>PCE export report preview</summary>
        <pre data-pce-export="report">{ "status": "waiting_for_pce_result" }</pre>
      </details>
    </section>
    <section class="pce-self-audit-panel">
      <div class="panel-title-row">
        <div>
          <h3>LUT Preview + Self-Audit</h3>
          <p data-pce-audit="description">Applies the actual exported CUBE table, re-matches against the reference, and keeps improving passes. Protected skin and highlight clipping are checked before accepting a pass.</p>
        </div>
        <span>BOUNDED FEEDBACK · BEST PASS KEPT</span>
      </div>
      <div class="pce-audit-grid">
        <div><small>Before LUT</small><strong data-pce-audit="before">—</strong></div>
        <div><small>After LUT</small><strong data-pce-audit="after">—</strong></div>
        <div><small>Improvement</small><strong data-pce-audit="delta">—</strong></div>
        <div><small>Audit time</small><strong data-pce-audit="duration">—</strong></div>
        <div class="wide"><small>Verdict</small><strong data-pce-audit="verdict">Waiting for analysis</strong></div>
        <div><small>Remaining temp</small><strong data-pce-audit="temperature">—</strong></div>
        <div><small>Remaining tint</small><strong data-pce-audit="tint">—</strong></div>
        <div><small>Remaining exposure</small><strong data-pce-audit="exposure">—</strong></div>
        <div><small>White confidence</small><strong data-pce-audit="white-confidence">—</strong></div>
        <div class="wide"><small>Residual white gains</small><strong data-pce-audit="white-gains">—</strong></div>
        <div><small>Black confidence</small><strong data-pce-audit="black-confidence">—</strong></div>
        <div class="wide"><small>Residual black offsets</small><strong data-pce-audit="black-offsets">—</strong></div>
        <div><small>Residual sat ratio</small><strong data-pce-audit="sat-ratio">—</strong></div>
        <div><small>Skin confidence</small><strong data-pce-audit="skin-confidence">—</strong></div>
        <div class="wide"><small>Residual skin power</small><strong data-pce-audit="skin-power">—</strong></div>
        <div class="wide"><small>Residual 6-vector shifts</small><strong data-pce-audit="vectors">—</strong></div>
      </div>
      <ul data-pce-audit="notes"><li>Run a PCE match to audit the generated LUT.</li></ul>
      <details><summary>Audit JSON / accepted and rejected passes</summary><pre data-pce-audit="raw">{ "status": "waiting_for_match" }</pre></details>
    </section>
    <details class="pce-json">
      <summary>Raw PCE JSON</summary>
      <pre data-pce="raw">{ "status": "waiting_for_images" }</pre>
    </details>
  `;

  const semanticProvider = new LocalPCESemanticProvider();
  window.addEventListener('pagehide', () => semanticProvider.close(), { once: true });
  const referenceInput = root.querySelector<HTMLInputElement>('[data-pce="reference-input"]')!;
  const paletteSelect = root.querySelector<HTMLSelectElement>('[data-pce="palette-preset"]')!;
  const sourceInput = root.querySelector<HTMLInputElement>('[data-pce="source-input"]')!;
  const referenceCanvas = root.querySelector<HTMLCanvasElement>('[data-pce="reference-canvas"]')!;
  const sourceCanvas = root.querySelector<HTMLCanvasElement>('[data-pce="source-canvas"]')!;
  const auditCanvas = root.querySelector<HTMLCanvasElement>('[data-pce-audit="preview-canvas"]')!;
  const analyzeButton = root.querySelector<HTMLButtonElement>('[data-pce="analyze"]')!;
  const rematchButton = root.querySelector<HTMLButtonElement>('[data-pce="rematch"]')!;
  const sampleButton = root.querySelector<HTMLButtonElement>('[data-pce="sample"]')!;
  const status = root.querySelector<HTMLElement>('[data-pce="status"]')!;
  const workingSpace = root.querySelector<HTMLSelectElement>('[data-pce="working-space"]')!;
  const targetEnabled = root.querySelector<HTMLInputElement>('[data-pce-target="enabled"]')!;
  const pickReferenceButton = root.querySelector<HTMLButtonElement>('[data-pce-target="pick-reference"]')!;
  const pickSourceButton = root.querySelector<HTMLButtonElement>('[data-pce-target="pick-source"]')!;
  const clearTargetButton = root.querySelector<HTMLButtonElement>('[data-pce-target="clear"]')!;
  let latestResult: SceneAwarePCEResult | undefined;
  let latestAudit: SelfAuditState | undefined;
  let reference: LoadedFrame | undefined;
  let uploadedReference: LoadedFrame | undefined;
  let source: LoadedFrame | undefined;
  let targetState: TargetColorState = { enabled: true };
  let pickerMode: 'reference' | 'source' | undefined;

  const updateReadyState = (): void => {
    analyzeButton.disabled = !(reference && source);
    if (reference && source) status.textContent = 'Ready for local PCE analysis.';
  };
  const renderTargetState = (): void => {
    targetState.enabled = targetEnabled.checked;
    const referenceSwatch = root.querySelector<HTMLElement>('[data-pce-target="reference-swatch"]')!;
    const sourceSwatch = root.querySelector<HTMLElement>('[data-pce-target="source-swatch"]')!;
    referenceSwatch.setAttribute('style', swatchStyle(targetState.reference));
    sourceSwatch.setAttribute('style', swatchStyle(targetState.source));
    root.querySelector<HTMLElement>('[data-pce-target="reference-rgb"]')!.textContent = colorText(targetState.reference);
    root.querySelector<HTMLElement>('[data-pce-target="source-rgb"]')!.textContent = colorText(targetState.source);
    const targetStatus = root.querySelector<HTMLElement>('[data-pce-target="status"]')!;
    const ready = targetState.enabled && targetState.reference && targetState.source;
    targetStatus.textContent = pickerMode
      ? `Click the ${pickerMode} image to sample the target color.`
      : ready
        ? 'Target color match will be included in preview and .cube export.'
        : targetState.enabled
          ? 'Pick both colors to enable targeted LUT correction.'
          : 'Target color match disabled.';
  };
  const resetAudit = (): void => {
    latestAudit = undefined;
    latestResult = undefined;
    rematchButton.disabled = true;
    root.querySelectorAll<HTMLButtonElement>('[data-pce-download]').forEach((button) => { button.disabled = true; });
    const preview = auditCanvas.getContext('2d');
    preview?.clearRect(0, 0, auditCanvas.width, auditCanvas.height);
    renderDetectionOverlays(root, undefined, false);
    root.querySelector<HTMLElement>('[data-pce="detection-mode"]')!.textContent = 'Re-analyze to update';
    renderTonalMatching(root);
    root.querySelector<HTMLElement>('[data-pce-audit="raw"]')!.textContent = '{ "status": "settings_or_images_changed" }';
    root.querySelector<HTMLElement>('[data-pce="raw"]')!.textContent = '{ "status": "reanalysis_required" }';
    root.querySelector<HTMLElement>('[data-pce-export="report"]')!.textContent = '{ "status": "reanalysis_required" }';
    for (const field of ['before', 'after', 'delta', 'duration'])
      root.querySelector<HTMLElement>(`[data-pce-audit="${field}"]`)!.textContent = '—';
    root.querySelector<HTMLButtonElement>('[data-pce-download="cube-v2"]')!.disabled = true;
    root.querySelector<HTMLElement>('[data-pce-audit="verdict"]')!.textContent = 'Waiting for analysis';
  };
  const loadInput = async (
    input: HTMLInputElement,
    canvas: HTMLCanvasElement,
    nameSelector: string,
    assign: (frame: LoadedFrame) => void,
  ): Promise<void> => {
    const file = input.files?.[0];
    if (!file) return;
    status.textContent = `Loading ${file.name}…`;
    try {
      const frame = await loadFile(file, canvas);
      assign(frame);
      resetAudit();
      if (canvas === referenceCanvas) targetState = { ...targetState, reference: undefined };
      if (canvas === sourceCanvas) targetState = { ...targetState, source: undefined };
      renderTargetState();
      root.querySelector<HTMLElement>(nameSelector)!.textContent =
        `${frame.name} · ${frame.imageData.width}×${frame.imageData.height}`;
      updateReadyState();
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : String(error);
    }
  };

  referenceInput.addEventListener('change', () => {
    void loadInput(referenceInput, referenceCanvas, '[data-pce="reference-name"]', (frame) => {
      reference = frame;
      uploadedReference = frame;
    });
  });
  const updateReferenceMode = (): void => {
    const preset = paletteSelect.value ? getPCEPalettePreset(paletteSelect.value) : undefined;
    referenceInput.hidden = Boolean(preset);
    const description = root.querySelector<HTMLElement>('[data-pce="preset-description"]')!;
    description.hidden = !preset;
    description.textContent = preset ? `${preset.description} Creative palette applies globally, including skin; score measures color adherence, not scene similarity.` : '';
    for (const key of ['ignore-green', 'protect-skin', 'match-midtones', 'six-vectors', 'tonal-ranges', 'use-detection']) {
      // Image-reference correspondence options are not controls on a global palette look.
      // Keep their checked values intact for when the user returns to image mode.
      const control = root.querySelector<HTMLInputElement>(`[data-pce="${key}"]`);
      if (control) control.disabled = Boolean(preset);
    }
    root.querySelector<HTMLElement>('[data-pce="reference-title"]')!.textContent = preset ? `Palette · ${preset.name}` : 'Reference image';
    reference = preset ? { imageData: createPCEPaletteFrame(preset.id), name: `${preset.name} · built-in sRGB palette` } : uploadedReference;
    targetState = { ...targetState, reference: undefined };
    pickerMode = undefined;
    resetAudit();
    if (reference) drawImageData(referenceCanvas, reference.imageData);
    else referenceCanvas.getContext('2d')?.clearRect(0, 0, referenceCanvas.width, referenceCanvas.height);
    root.querySelector<HTMLElement>('[data-pce="reference-name"]')!.textContent = reference
      ? `${reference.name} · ${reference.imageData.width}×${reference.imageData.height}` : 'Choose an image';
    renderTargetState();
    updateReadyState();
    if (!source) status.textContent = preset ? `${preset.name} palette ready. Load your source image to match.` : 'Load a source and choose a reference image or palette preset.';
    else if (!reference) status.textContent = 'Choose a reference image or palette preset.';
  };
  paletteSelect.addEventListener('change', updateReferenceMode);
  sourceInput.addEventListener('change', () => {
    void loadInput(sourceInput, sourceCanvas, '[data-pce="source-name"]', (frame) => {
      source = frame;
    });
  });
  sampleButton.addEventListener('click', () => {
    reference = sampleFrame(referenceCanvas, false);
    uploadedReference = reference;
    paletteSelect.value = '';
    for (const key of ['ignore-green', 'protect-skin', 'match-midtones', 'six-vectors', 'tonal-ranges', 'use-detection'])
      root.querySelector<HTMLInputElement>(`[data-pce="${key}"]`)!.disabled = false;
    referenceInput.hidden = false;
    root.querySelector<HTMLElement>('[data-pce="preset-description"]')!.hidden = true;
    root.querySelector<HTMLElement>('[data-pce="reference-title"]')!.textContent = 'Reference image';
    source = sampleFrame(sourceCanvas, true);
    targetState = { enabled: targetEnabled.checked };
    renderTargetState();
    resetAudit();
    root.querySelector<HTMLElement>('[data-pce="reference-name"]')!.textContent = 'Sample reference · 480×270';
    root.querySelector<HTMLElement>('[data-pce="source-name"]')!.textContent = 'Sample source · 480×270';
    updateReadyState();
  });
  pickReferenceButton.addEventListener('click', () => {
    pickerMode = 'reference';
    renderTargetState();
  });
  pickSourceButton.addEventListener('click', () => {
    pickerMode = 'source';
    renderTargetState();
  });
  clearTargetButton.addEventListener('click', () => {
    pickerMode = undefined;
    targetState = { enabled: targetEnabled.checked };
    resetAudit();
    renderTargetState();
  });
  targetEnabled.addEventListener('change', () => {
    targetState.enabled = targetEnabled.checked;
    resetAudit();
    renderTargetState();
  });
  referenceCanvas.addEventListener('click', (event) => {
    if (pickerMode !== 'reference' || !reference) return;
    const point = canvasPoint(event, referenceCanvas);
    targetState.reference = { ...point, rgb: pickAverageColor(reference.imageData, point.x, point.y) };
    pickerMode = undefined;
    resetAudit();
    renderTargetState();
  });
  sourceCanvas.addEventListener('click', (event) => {
    if (pickerMode !== 'source' || !source) return;
    const point = canvasPoint(event, sourceCanvas);
    targetState.source = { ...point, rgb: pickAverageColor(source.imageData, point.x, point.y) };
    pickerMode = undefined;
    resetAudit();
    renderTargetState();
  });
  const runAnalysis = async (): Promise<void> => {
    if (!reference || !source) return;
    analyzeButton.disabled = true;
    rematchButton.disabled = true;
    sampleButton.disabled = true;
    root.querySelectorAll<HTMLButtonElement>('[data-pce-target], [data-pce-download]').forEach((button) => { button.disabled = true; });
    root.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.pce-controls input, .pce-controls select, [data-pce="reference-input"], [data-pce="source-input"], [data-pce="palette-preset"]').forEach((element) => { element.disabled = true; });
    status.textContent = 'Comparing locally…';
    const started = performance.now();
    try {
      const options = currentPCEOptions(root, workingSpace);
      const audit = await runPCEMatchPipeline(source.imageData, reference.imageData, options, semanticProvider, {
        targetedMatches: targetedMatchesFromState(targetState),
        onProgress: (message) => { status.textContent = message; },
      });
      const result = audit.baselineResult;
      latestResult = result;
      const duration = performance.now() - started;
      renderResult(root, result, duration, options.lutSize);
      latestAudit = audit;
      drawImageData(auditCanvas, audit.correctedSource);
      renderSelfAudit(root, result, audit, duration);
      renderSixVectors(root, result);
      renderTonalMatching(root, result, audit.result);
      renderDetectionSummary(root, result);
      renderDetectionOverlays(root, audit.semanticContext, root.querySelector<HTMLInputElement>('[data-pce="show-detection"]')!.checked);
      root.querySelector<HTMLElement>('[data-pce-export="report"]')!.textContent = JSON.stringify({ ...exporter.generateReport(result, { size: options.lutSize }), referencePreset: audit.report.referencePreset, audit: audit.report }, null, 2);
      status.textContent = `Match + audit complete · ${audit.report.acceptedPasses} improving pass(es) kept · ${duration.toFixed(0)} ms`;
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : String(error);
    } finally {
      analyzeButton.disabled = false;
      rematchButton.disabled = !latestAudit;
      sampleButton.disabled = false;
      root.querySelectorAll<HTMLButtonElement>('[data-pce-target]').forEach((button) => { button.disabled = false; });
      root.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.pce-controls input, .pce-controls select, [data-pce="reference-input"], [data-pce="source-input"], [data-pce="palette-preset"]').forEach((element) => { element.disabled = false; });
      if (paletteSelect.value) for (const key of ['ignore-green', 'protect-skin', 'match-midtones', 'six-vectors', 'tonal-ranges', 'use-detection'])
        root.querySelector<HTMLInputElement>(`[data-pce="${key}"]`)!.disabled = true;
    }
  };
  analyzeButton.addEventListener('click', () => { void runAnalysis(); });
  rematchButton.addEventListener('click', () => { void runAnalysis(); });
  root.querySelector<HTMLSelectElement>('[data-pce="tonal-view"]')!.addEventListener('change', () => {
    renderTonalMatching(root, latestResult, latestAudit?.result);
  });
  root.querySelector<HTMLInputElement>('[data-pce="show-detection"]')!.addEventListener('change', (event) => {
    renderDetectionOverlays(root, latestAudit?.semanticContext, (event.target as HTMLInputElement).checked);
  });
  root.querySelector<HTMLInputElement>('[data-pce="use-detection"]')!.addEventListener('change', (event) => {
    if (!(event.target as HTMLInputElement).checked) semanticProvider.close();
  });
  root.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.pce-controls input:not([data-pce="show-detection"]), .pce-controls select').forEach((element) => {
    element.addEventListener('change', resetAudit);
  });
  root.querySelectorAll<HTMLButtonElement>('[data-pce-download]').forEach((button) => {
    button.addEventListener('click', () => {
      if (!latestResult) return;
      const kind = button.dataset.pceDownload;
      const cdl = exporter.generateCDL(latestResult);
      if (kind === 'cc') {
        downloadText('limonade-pce-suggestion.cc', 'application/xml', exportCDL(cdl, {
          id: 'limonade_pce_suggestion',
          name: 'Limonade PCE Suggested CDL',
        }));
      } else if (kind === 'ccc') {
        downloadText('limonade-pce-suggestion.ccc', 'application/xml', exportCCC([cdl], {
          id: 'limonade_pce_collection',
          name: 'Limonade PCE Suggested CDL Collection',
        }));
      } else if (kind === 'cube') {
        if (latestAudit) downloadText('pre-match-first-pass.cube', 'text/plain', latestAudit.firstCube);
      } else if (kind === 'cube-v2' && latestAudit) {
        downloadText('pre-match.cube', 'text/plain', latestAudit.cube);
      } else if (kind === 'report') {
        downloadText('limonade-pce-export-report.json', 'application/json', JSON.stringify(
          { ...exporter.generateReport(latestResult, { size: (latestAudit?.report.lut.size as 17 | 33 | undefined) ?? PCE_MATCH_DEFAULTS.lutSize }), referencePreset: latestAudit?.report.referencePreset, audit: latestAudit?.report, semanticAnalysis: latestResult.semanticAnalysis },
          null,
          2,
        ));
      }
    });
  });
  renderTargetState();
}
